
import React from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { RefreshCw, AlertTriangle, CheckCircle } from 'lucide-react';

interface AuthDebugPanelProps {
  show?: boolean;
}

export const AuthDebugPanel: React.FC<AuthDebugPanelProps> = ({ show = false }) => {
  const { 
    user, 
    session, 
    isAuthenticated, 
    isLoading, 
    refreshSession, 
    validateSession,
    forceSignOut 
  } = useAuth();

  if (!show) return null;

  const handleValidateSession = async () => {
    const isValid = await validateSession();
    console.log('Session validation result:', isValid);
  };

  return (
    <Card className="fixed bottom-4 right-4 w-80 z-50 border-2 border-orange-500">
      <CardHeader className="pb-3">
        <CardTitle className="text-sm flex items-center gap-2">
          <AlertTriangle className="h-4 w-4" />
          Auth Debug Panel
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid grid-cols-2 gap-2 text-xs">
          <div>
            <span className="font-medium">Authenticated:</span>
            <Badge variant={isAuthenticated ? "default" : "destructive"} className="ml-1">
              {isAuthenticated ? "Yes" : "No"}
            </Badge>
          </div>
          <div>
            <span className="font-medium">Loading:</span>
            <Badge variant={isLoading ? "secondary" : "outline"} className="ml-1">
              {isLoading ? "Yes" : "No"}
            </Badge>
          </div>
          <div>
            <span className="font-medium">User:</span>
            <Badge variant={user ? "default" : "destructive"} className="ml-1">
              {user ? "Present" : "None"}
            </Badge>
          </div>
          <div>
            <span className="font-medium">Session:</span>
            <Badge variant={session ? "default" : "destructive"} className="ml-1">
              {session ? "Present" : "None"}
            </Badge>
          </div>
        </div>
        
        {user && (
          <div className="text-xs">
            <span className="font-medium">User ID:</span>
            <p className="text-muted-foreground font-mono truncate">{user.id}</p>
          </div>
        )}
        
        {session && (
          <div className="text-xs">
            <span className="font-medium">Session Expires:</span>
            <p className="text-muted-foreground">
              {session.expires_at ? new Date(session.expires_at * 1000).toLocaleString() : 'Never'}
            </p>
          </div>
        )}

        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={handleValidateSession}>
            <CheckCircle className="h-3 w-3 mr-1" />
            Validate
          </Button>
          <Button size="sm" variant="outline" onClick={refreshSession}>
            <RefreshCw className="h-3 w-3 mr-1" />
            Refresh
          </Button>
          <Button size="sm" variant="destructive" onClick={forceSignOut}>
            <AlertTriangle className="h-3 w-3 mr-1" />
            Force Out
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};
